import React from 'react';
import { Target, Zap, Award, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import ResponsiveCarousel from './ResponsiveCarousel';

const About = () => {

  const values = [
    {
      icon: Target,
      title: 'Precision Detection',
      description: 'Our flagship UL101 ultrasonic sensor delivers unparalleled accuracy in component troubleshooting, transforming time-consuming guesswork into precise, data-driven diagnostics that reduce false positives and minimize aircraft downtime.',
      logos: [
        { 
          src: '/images/ctrl-bar-logo-no-border-.png',
          alt: 'CTRL Systems',
          height: 'h-14'
        }
      ],
      buttonLink: '/products/ul101'
    },
    {
      icon: Zap,
      title: 'Real-Time Analysis',
      description: 'FOTRIC H-Series acoustic imaging cameras, equipped with advanced NaviPdm Venus technology, provide instant acoustic visualization for rapid airframe and powerplant diagnostics with industry-leading thermal and acoustic integration.',
      logos: [
        { 
          src: '/images/fotric-logo-transparent.png',
          alt: 'FOTRIC',
          height: 'h-16'
        }
      ],
      buttonLink: '/products/fotric'
    },
    {
      icon: Award,
      title: 'Industry Trust',
      description: 'Trusted by aviation leaders including Southwest Airlines and Boeing Charlie Works for critical maintenance operations. Our proven solutions are deployed across major airline fleets and MRO facilities worldwide.',
      logos: [
        { 
          src: '/images/boeing-logo-transparent.png',
          alt: 'Boeing',
          height: 'h-14'
        },
        { 
          src: '/images/southwest_airlines_logo_2014.svg.png',
          alt: 'Southwest Airlines',
          height: 'h-7'
        }
      ]
    },
    {
      icon: Users,
      title: 'Valued Partners',
      description: 'Barfield and AvionTEq serve as our preferred distribution partners, delivering CTRL solutions to the global aviation market. Dalcomm Tech is our exclusive headset partner, providing seamless communication integration for aviation maintenance and ramp professionals.',
      logos: [
        { 
          src: '/images/barfield-colored-logo-transparent.jpg',
          alt: 'Barfield',
          height: 'h-9'
        },
        { 
          src: '/images/avionteq-logo.jpg',
          alt: 'AvionTEq',
          height: 'h-10'
        },
        { 
          src: '/images/img_0290.jpeg',
          alt: 'Dalcomm Tech',
          height: 'h-12'
        }
      ]
    }
  ];

  return (
    <section id="about" className="py-14 sm:py-20 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mission Content with Image */}
        <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-6 sm:p-8 lg:p-12 mb-10 sm:mb-16 border border-blue-100">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1e2d3b] mb-4 sm:mb-6">
                We're On a Mission to Change Aviation Maintenance.
              </h2>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-4 sm:mb-6">
                CTRL Systems partners with aviation OEMs, operators, and MROs to integrate ultrasonic detection and acoustic imaging technology into aircraft maintenance operations. We provide professional-grade ultrasonic sensors and acoustic cameras that transform component troubleshooting from time-consuming guesswork into precise, data-driven diagnostics.
              </p>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-4 sm:mb-6">
                Today, we are the world's leading provider of ultrasonic troubleshooting solutions to the commercial aviation industry. By combining advanced ultrasonic sensor arrays with intelligent acoustic cameras, we enable faster issue detection, reduced aircraft downtime, and enhanced maintenance efficiency.
              </p>
              <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                Our vision for the future is to become one of the world's recognized leaders in A.I.-enabled CBM solutions for aerospace component reliability.
              </p>
            </div>
            <div className="relative">
              <img 
                src="/images/img_3536.jpeg"
                alt="Technician inspecting aircraft landing gear"
                className="rounded-2xl shadow-2xl w-full max-h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/10 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>

        <ResponsiveCarousel
          breakpoints={{ base: 1, sm: 2, lg: 3 }}
          ariaLabel="About ctrl.aero values"
          dataTestId="about-carousel"
          autoPlay
          autoPlayInterval={15000}
        >
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div 
                key={value.title}
                className="group p-6 bg-white rounded-2xl border-2 border-slate-100 hover:border-blue-500 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 flex flex-col h-full"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-500/30">
                  <Icon className="h-7 w-7 text-white" />
                </div>
                <h4 className="text-xl font-bold text-[#1e2d3b] mb-3">{value.title}</h4>
                <p className="text-slate-600 leading-relaxed mb-6 flex-grow">{value.description}</p>
                
                {/* Company Logos */}
                <div className="flex flex-col items-center justify-center gap-3 pt-4 border-t border-slate-100 min-h-[60px]">
                  {value.logos.map((logo, logoIndex) => (
                    logo.placeholder ? (
                      <div 
                        key={logoIndex}
                        className={`${logo.height} px-3 py-1 bg-slate-100 rounded flex items-center justify-center`}
                      >
                        <span className="text-xs font-bold text-slate-600 tracking-wide">
                          {logo.name}
                        </span>
                      </div>
                    ) : (
                      <img 
                        key={logoIndex}
                        src={logo.src}
                        alt={logo.alt}
                        className={`${logo.height} w-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-300`}
                      />
                    )
                  ))}
                  
                  {/* Learn More Button */}
                  {value.buttonLink && (
                    <Link
                      to={value.buttonLink}
                      className="mt-2 inline-flex items-center justify-center rounded-md bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm px-8 py-2 font-medium transition-colors"
                      data-testid={`about-learn-more-${value.title.toLowerCase().replace(/\s+/g,'-')}`}
                    >
                      Learn More
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </ResponsiveCarousel>
      </div>
    </section>
  );
};

export default About;
